import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle } from 'lucide-react';
import { GenreSuggestion } from '@/services/enhancedSpotifyService';
import { GenreCreationStatus } from '@/hooks/useSpotifyGenreDetection';

interface GenreSuggestionsListProps {
  /** Genre suggestions to display */
  suggestions: GenreSuggestion[];

  /** Currently selected genre IDs */
  selectedGenreIds?: string[];

  /** Whether genre detection is in progress */
  isLoading?: boolean;

  /** Genre creation status */
  creationStatus?: GenreCreationStatus;

  /** Callback when a genre is selected */
  onGenreSelect?: (suggestion: GenreSuggestion) => void;

  /** Show confidence badges */
  showConfidence?: boolean;

  /** Show category labels */
  showCategory?: boolean;

  /** Maximum number of suggestions to show */
  maxDisplay?: number;

  /** Custom empty state message */
  emptyMessage?: string;
}

/**
 * Reusable component for displaying Spotify genre suggestions
 *
 * Provides consistent UI for genre suggestions across all forms:
 * - Confidence indicators
 * - New genre badges
 * - Creation status
 * - Loading states
 * - Selection interaction
 *
 * @example
 * ```tsx
 * <GenreSuggestionsList
 *   suggestions={suggestions}
 *   selectedGenreIds={selectedGenres}
 *   isLoading={isDetecting}
 *   creationStatus={creationStatus}
 *   onGenreSelect={(genre) => handleGenreSelect(genre)}
 *   showConfidence
 * />
 * ```
 */
export const GenreSuggestionsList: React.FC<GenreSuggestionsListProps> = ({
  suggestions,
  selectedGenreIds = [],
  isLoading = false,
  creationStatus,
  onGenreSelect,
  showConfidence = true,
  showCategory = true,
  maxDisplay,
  emptyMessage = "We couldn't automatically detect a genre for this track.",
}) => {
  const displaySuggestions = maxDisplay ? suggestions.slice(0, maxDisplay) : suggestions;

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800 border-green-200';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getConfidenceText = (confidence: number) => {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  };

  // Show genre creation status if creating or recently created
  if (creationStatus?.isCreating) {
    return (
      <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
          <p className="text-sm text-indigo-800 font-medium">
            Creating new genres from Spotify...
          </p>
        </div>
      </div>
    );
  }

  if (creationStatus && creationStatus.total > 0 && !creationStatus.isCreating) {
    return (
      <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-3">
        <p className="text-sm text-green-800 font-medium">
          ✨ Genre Processing Complete!
        </p>
        <div className="text-xs text-green-700 mt-1 flex gap-3">
          {creationStatus.created > 0 && (
            <span>🆕 {creationStatus.created} new genre{creationStatus.created > 1 ? 's' : ''} created</span>
          )}
          {creationStatus.found > 0 && (
            <span>✅ {creationStatus.found} existing genre{creationStatus.found > 1 ? 's' : ''} found</span>
          )}
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <p className="text-sm text-blue-800">
            Analyzing track to detect genres...
          </p>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!displaySuggestions || displaySuggestions.length === 0) {
    return (
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 font-medium">
              {emptyMessage}
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              You can manually select a genre if needed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show suggestions list
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        {displaySuggestions.length > 1
          ? 'We detected the following genres. Click to select:'
          : 'Detected genre:'}
      </p>

      <div className="grid gap-2">
        {displaySuggestions.map((suggestion, index) => {
          const isSelected = selectedGenreIds.includes(suggestion.id);

          return (
            <div
              key={suggestion.id}
              className={`p-3 rounded-lg border transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : onGenreSelect
                  ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                  : 'border-gray-200'
              }`}
              onClick={() => onGenreSelect?.(suggestion)}
              role={onGenreSelect ? 'button' : undefined}
              tabIndex={onGenreSelect ? 0 : undefined}
              onKeyDown={(e) => {
                if (onGenreSelect && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onGenreSelect(suggestion);
                }
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Confidence badge for first suggestion */}
                  {showConfidence && index === 0 && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${getConfidenceColor(suggestion.confidence)}`}
                    >
                      {getConfidenceText(suggestion.confidence)} Confidence
                    </Badge>
                  )}

                  {/* New genre indicator */}
                  {suggestion.reason.toLowerCase().includes('new') && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-green-50 text-green-700 border-green-200"
                    >
                      🆕 New
                    </Badge>
                  )}

                  {/* Genre name */}
                  <span className="font-medium text-gray-900">
                    {suggestion.name}
                  </span>
                </div>

                {/* Category and reason */}
                <div className="text-right flex-shrink-0">
                  {showCategory && (
                    <p className="text-xs text-gray-500">{suggestion.category}</p>
                  )}
                  <p className="text-xs text-gray-400">{suggestion.reason}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Show count if truncated */}
      {maxDisplay && suggestions.length > maxDisplay && (
        <p className="text-xs text-gray-500 text-center">
          +{suggestions.length - maxDisplay} more genre{suggestions.length - maxDisplay > 1 ? 's' : ''} available
        </p>
      )}
    </div>
  );
};

export default GenreSuggestionsList;
